
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

import WriteOffsFilters from '@/modules/TmcModule/components/WriteOffsFilters/index.vue'

// constants
import { WriteOffsColumns } from '@/core/constants/columns/TmcWriteOffs.columns'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { WriteOffT, WriteOffFullItemT, WriteOffsGetListParams } from '@/core/types/tmc.types'

// realisations
import { tmc } from '@/core/realisations/tmc.realisation'

// composable
import useAccount from '@/common/composable/useAccount'
import { useFile } from '@/common/composable/useFile'
import useNotifications from '@/common/composable/useNotifications'

const MAX_NAME_LENGTH_IN_TABLE = 25

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'table-scroll-wrapper': TableScrollWrapper,
        'the-table': TheTable,
        WriteOffsFilters,
        Cheap,
    },
    setup() {
        const { havePermissions } = useAccount()
        const { saveFile } = useFile()
        const notifications = useNotifications()

        const state = reactive({
            writeoffs: new DataState<WriteOffT>({}),
        })

        const filters_state = reactive({
            additionalServiceTypeId: null as number | null,
            code1C: null as string | null,
            warehouseId: null as number | null,
            writeOffTypeId: null as number | null,
            invoiceNumber: null as string | null,
            dateFrom: null as string | null,
            dateTo: null as string | null,
            inventoryItemIds: [] as number[],
            payerCompanyIds: [] as number[],
            //
            limit: 50,
        })

        const loadWriteoffs = async (page = 1) => {
            state.writeoffs.pagination.current_page = page

            try {
                state.writeoffs.is_loading = true

                const params: WriteOffsGetListParams = {
                    page,
                    ...filters_state,
                }

                const response = await tmc.getWriteOffs(params)

                state.writeoffs.list = response.data
                state.writeoffs.pagination.total_pages = response.meta.last_page
                state.writeoffs.pagination.total_items = response.meta.total

                state.writeoffs.is_failed = false
                state.writeoffs.failure.error_state.is_cancel = false
            } catch (error) {
                state.writeoffs.failure.catchError(error)

                if (!state.writeoffs.failure.error_state.is_cancel) {
                    state.writeoffs.is_failed = true
                }
            } finally {
                if (!state.writeoffs.failure.error_state.is_cancel) {
                    state.writeoffs.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadWriteoffs(state.writeoffs.pagination.current_page)
        }

        onBeforeMount(loadWriteoffs)

        const getAmountSum = (items: WriteOffFullItemT[]): number => {
            let sum = 0

            items.forEach((item) => {
                sum += item.amount
            })

            return sum
        }

        const cutName = (name: string): string => {
            if (name && name.length > MAX_NAME_LENGTH_IN_TABLE) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, MAX_NAME_LENGTH_IN_TABLE - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const onSubmitFilters = (filters: WriteOffsGetListParams) => {
            filters_state.additionalServiceTypeId = filters.additionalServiceTypeId
            filters_state.code1C = filters.code1C
            filters_state.warehouseId = filters.warehouseId
            filters_state.writeOffTypeId = filters.writeOffTypeId
            filters_state.invoiceNumber = filters.invoiceNumber
            filters_state.dateFrom = filters.dateFrom || null
            filters_state.dateTo = filters.dateTo || null
            filters_state.inventoryItemIds = filters.inventoryItemIds
            filters_state.payerCompanyIds = filters.payerCompanyIds

            loadWriteoffs(1)
        }

        const onResetFilters = () => {
            filters_state.additionalServiceTypeId = null
            filters_state.code1C = null
            filters_state.warehouseId = null
            filters_state.writeOffTypeId = null
            filters_state.invoiceNumber = null
            filters_state.dateFrom = null
            filters_state.dateTo = null
            filters_state.inventoryItemIds = []
            filters_state.payerCompanyIds = []

            loadWriteoffs(1)
        }

        const exportExcel = async () => {
            try {
                state.writeoffs.is_loading = true
                const response = await tmc.exportExcelWriteoffs(filters_state)
                saveFile(response, 'Списание')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                state.writeoffs.is_loading = false
            }
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            return dateFirst.split('-').reverse().join('.')
        }

        return {
            WriteOffsColumns,
            state,
            filters_state,
            loadWriteoffs,
            havePermissions,
            getAmountSum,
            cutName,
            onSubmitFilters,
            onResetFilters,
            exportExcel,
            onLimitChange,
            formatDate,
        }
    },
})
